/**
 * The conversation's calls, typed from the generated contract (`KLQ-20260820-001` phase 1).
 *
 * THREE THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **The feed and the thread are two resources, not one endpoint with a flag.**
 *    `GET /projects/{id}/feed` is a mixed stream across many drawings, scoped by a tree node
 *    and paged by cursor. `GET /projects/{id}/comments` is one drawing's conversation, flat,
 *    whole, and still requires its `slot_id`. A single endpoint whose shape changed with a
 *    parameter is what `MCK-20260812-006` J asks to be split, and it was.
 * 2. **A feed entry is tagged.** `kind` is `comment` or `version` today; reviews and tasks
 *    are the third and fourth and both are specced. Anything reading an entry branches on the
 *    tag and must render an unrecognised one as a plain row rather than crashing — that is
 *    what makes the next kind additive.
 * 3. **A comment needs no pin.** `pin_id` is nullable on the way in and out, which is the
 *    whole reason this half of the wave ships before the canvas exists.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type FeedEntry = Schemas['FeedEntryResponse'];
export type FeedComment = Schemas['FeedCommentResponse'];
export type FeedVersion = Schemas['FeedVersionResponse'];
export type FeedPage = Schemas['Page_FeedEntryResponse_'];
export type Comment = Schemas['CommentResponse'];
/** Who a comment is written for — `internal` (the author's studio) or `project`. */
export type Audience = Schemas['Audience'];
export type CommentPage = Schemas['Page_CommentResponse_'];

export interface FeedQuery {
  /**
   * The slice (`KLQ-20260820-005`). Any hierarchy nodes: a drawing is its own thread, a
   * container is its whole subtree, several are their union, an empty list is the project.
   * Sent as a repeated `under=` parameter, which is what the API's filter rules mean by OR
   * within one field.
   */
  readonly under?: readonly string[] | undefined;
  readonly q?: string | undefined;
  readonly cursor?: string | undefined;
}

/**
 * What happened under one node, newest first.
 *
 * The query object is spread conditionally rather than built with `undefined` values:
 * `exactOptionalPropertyTypes` is on, and an explicit `undefined` would serialise as an empty
 * parameter the server then has to decide about.
 */
export function readFeed(projectId: string, query: FeedQuery): Promise<FeedPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/feed', {
      params: {
        path: { project_id: projectId },
        query: {
          ...(query.under === undefined || query.under.length === 0
            ? {}
            : { under: [...query.under] }),
          ...(query.q === undefined || query.q === '' ? {} : { q: query.q }),
          ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
        },
      },
    }),
  );
}

/** One drawing's conversation, oldest first, threaded flat on `parent_comment_id`. */
export function listComments(projectId: string, slotId: string): Promise<CommentPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/comments', {
      params: { path: { project_id: projectId }, query: { slot_id: slotId } },
    }),
  );
}

export interface CommentBody {
  readonly slot_id: string;
  readonly body: string;
  readonly parent_comment_id?: string;
  /**
   * Optional, and the omission means something: *"not named"* is not the same as
   * *"internal"* (`MCK-20260814-001`). A reply inherits its parent's audience and naming a
   * different one is a `409`, so the composer sends this on a top-level comment and omits it
   * on a reply rather than guessing the parent's value and asking the server to agree.
   */
  readonly audience?: Audience;
}

/**
 * Say something about a drawing.
 *
 * No `pin_id` from this screen: a pin is placed on the canvas, and the canvas is phase 3. A
 * loose comment is a first-class row and always was.
 *
 * `version_id_when_created` is deliberately not sent — the server reads the slot's current
 * version at the moment of the write, so a tab left open across a publish cannot attribute
 * today's comment to yesterday's sheet.
 */
export function createComment(projectId: string, body: CommentBody): Promise<Comment> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/comments', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * Mark one comment answered.
 *
 * Does **not** resolve its pin: the pin is the topic, the comment is one thing said about it
 * (`annotations/router.py`). Nothing here infers one from the other.
 */
export function resolveComment(commentId: string): Promise<Comment> {
  return unwrap(
    api.POST('/api/v1/comments/{comment_id}/resolve', {
      params: { path: { comment_id: commentId } },
    }),
  );
}
